import Image from "next/image";
import { useState } from "react";
import styles from "./Mosttrusted.module.scss";
import MosttrustedMobile from "./MosttrustedMobile";

interface LeadershipPillarsProps {
  block?: any;
}
const Mosttrusted: React.FC<LeadershipPillarsProps> = ({ block }) => {
  // First card open by default
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL || "";

  return (
    <>
      <section>
        <div className="leader-banner bg-background-dark py-[56px] lg:pb-[80px] xl:py-[120px] relative z-0">
          {/* Shapes */}
          <div className="banner-shape-1 absolute top-[0px] left-0 z-[1] w-[50%] md:w-[40%]">
            <Image
              src="/images/banner-left-layer-mobile.png"
              alt="Banner Shape"
              width={571}
              height={571}
              className="w-full h-full md:hidden"
            />
            <Image
              src="/images/banner-left-layer-tablet.png"
              alt="Banner Shape"
              width={571}
              height={571}
              className="w-full h-full hidden md:block lg:hidden"
            />
            <Image
              src="/images/banner-left-layer-desktop.png"
              alt="Banner Shape"
              width={571}
              height={571}
              className="w-full h-full hidden lg:block"
            />
          </div>
          <div className="banner-shape-1 absolute bottom-0 right-0 z-[-1] ">
            <Image
              src="/images/banner-right-layer-mobile.png"
              alt="Banner Shape"
              width={471}
              height={471}
              className="object-cover w-full h-full md:hidden"
            />
            <Image
              src="/images/banner-right-layer-tablet.png"
              alt="Banner Shape"
              width={471}
              height={471}
              className="object-cover w-full h-full hidden md:block lg:hidden"
            />
            <Image
              src="/images/banner-right-layer-desktop.png"
              alt="Banner Shape"
              width={471}
              height={471}
              className="object-cover w-full h-full hidden lg:block"
            />
          </div>

          <div className="container">
            <div className="heading xl:mb-[50px] md:mb-[30px] mb-[24px]">
              <h2 className="text-heading-1 font-bold text-white text-center">
                {block?.title ?? "Leadership Pillars"}
              </h2>
            </div>
            <div className="text-secondary-text-white text-subtitle-2 text-center mt-[10px] mb-[40px] max-w-[812px] mx-auto">
              {block?.short_title}
            </div>

            {/* mobile */}
            <MosttrustedMobile block={block} />

            {/* desktop */}
            <div className="hidden lg:flex gap-[30px]">
              {block?.points?.map((card: any, index: number) => {
                const isFirst = index === 0;
                const isOpen =
                  hoveredIndex !== null
                    ? hoveredIndex === index // if hovering, only that card opens
                    : isFirst; // no hover → first stays open
                const imgUrl = card?.image?.data?.attributes?.url ?? "";
                const img = imgUrl
                  ? baseUrl + imgUrl
                  : `/images/leadership/pillars-icon${index > 0 ? "-" + (index + 1) : ""}.svg`;
                return (
                  <div
                    key={index}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    className={`relative cursor-pointer overflow-hidden border border-[#40424C] z-[10] transition-all duration-800 linear before:content-[''] before:absolute before:inset-0 before:bg-no-repeat before:bg-[length:100%_100%] ${isOpen ? "w-[85%] bg-[#E6F3FD] p-[32px] before:bg-[url('/images/leadership/card-shape-bg.png')]" : "w-[15%] bg-gradient-to-b from-[#040405] to-[#0A0A0F] p-[15px_15px_32px_15px] before:bg-none"}`}
                    style={{ height: "500px" }}
                  >
                    {/* Background overlay */}
                    <div className="absolute inset-0 before:content-[''] before:absolute before:inset-0 before:bg-[url('/images/leadership/card-shape-bg-2.png')] before:bg-no-repeat before:bg-cover before:opacity-30"></div>

                    {/* Open State */}
                    {isOpen ? (
                      <div className="z-10 h-full flex flex-col items-start justify-between transition-all duration-500">
                        <div className="absolute top-[-20px] right-[-20px]">
                          <Image
                            width={130}
                            height={263}
                            alt="Leadership Pillars open"
                            src="/images/leadership/card-shape-1.svg"
                            className="object-cover h-auto"
                          />
                        </div>

                        <div className="relative inline-block mb-[20px] left-[-10px] bottom-[10px]">
                          <Image
                            width={116}
                            height={116}
                            alt={"Leadership Pillars shape" + (index + 1)}
                            src="/images/leadership/pillar-img-shape.png"
                            className="object-cover w-[80px] h-[80px] xl:w-[116px] xl:h-[116px]"
                          />
                          {card?.image?.data?.attributes?.url && (
                            <Image
                              width={60}
                              height={60}
                              alt={"pillars-icon" + (index + 1)}
                              src={baseUrl + card?.image?.data?.attributes?.url}
                              className="object-cover w-[40px] h-[40px] xl:w-[60px] xl:h-[55px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"
                            />
                          )}
                        </div>

                        <div className="relative z-1">
                          <h4 className="text-heading-5 font-bold text-primary-text mb-[16px]">
                            {card?.title}
                          </h4>
                          <p
                            className="text-[20px] leading-[28.2px] xl:text-[24px] xl:leading-[31.2px] font-regular text-secondary-text mb-0 line-clamp-7"
                            dangerouslySetInnerHTML={{
                              __html:
                                card?.text?.replace(/^<p>|<\/p>$/g, "") || "",
                            }}
                          ></p>
                        </div>
                      </div>
                    ) : (
                      /* Closed State */
                      <div className="relative z-10 h-full flex flex-col items-center justify-between">
                        <div className="relative pt-[35px] mx-auto pb-[20px]">
                          <Image
                            src={img}
                            width={41}
                            height={41}
                            alt={"pillars-icon" + (index + 10)}
                            className="w-[32px] h-[32px] xl:w-[41px] xl:h-[41px] object-contain mx-auto"
                          />
                          <Image
                            height={2}
                            width={100}
                            alt={"pillars-icon" + (index + 20)}
                            className="w-full object-contain relative bottom-[20px]"
                            src="/images/leadership/shape-border.png"
                          />
                        </div>
                        <div className="relative">
                          <h4
                            className={`text-[22px] leading-[28.2px] xl:text-[24px] xl:leading-[31.2px] font-bold text-white ${styles.writingSideways}`}
                          >
                            {card.title}
                          </h4>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Mosttrusted;
